/**
 * CloudWatch RUM bootstrap for the dashboard.
 *
 * This complements the `@authe/ui-core` `ObservabilityProvider` (mounted in
 * `providers.tsx` for Sentry + log forwarding). We can't rely on it for RUM:
 * `@authe/fe-commons/observability` loads `aws-rum-web` with a
 * framework-agnostic `import(/* @vite-ignore *​/ "aws-rum-web")`, which Vite
 * leaves as a bare specifier — unresolvable in the built browser bundle, so the
 * library never loads and RUM silently no-ops. A LITERAL dynamic import here
 * makes Vite bundle + code-split the library so RUM actually initialises.
 * `aws-rum-web` auto-captures page loads, performance, JS errors, and HTTP
 * calls, so no per-event wiring is needed.
 *
 * Reads the RUM ids resolved into `appEnv` (`VITE_RUM_APP_ID` /
 * `VITE_RUM_IDENTITY_POOL_ID` / `VITE_RUM_REGION`). Unset ids (local dev) → no-op.
 */
import type { AutheEnv } from "@authe/fe-commons/env";

type AwsRumCtor = new (
  applicationId: string,
  applicationVersion: string,
  applicationRegion: string,
  config: Record<string, unknown>,
) => unknown;

let started = false;

/** Initialise CloudWatch RUM once, if the RUM env ids are configured. */
export async function initRum(env: AutheEnv): Promise<void> {
  if (started) return;
  if (!env.rumAppId || !env.rumIdentityPoolId || !env.rumRegion) return;
  started = true;

  try {
    // Literal specifier → Vite bundles aws-rum-web into its own lazy chunk
    // (unlike fe-commons' @vite-ignore variable import, which stays unbundled).
    const { AwsRum } = (await import("aws-rum-web")) as unknown as { AwsRum: AwsRumCtor };
    new AwsRum(env.rumAppId, "1.0.0", env.rumRegion, {
      sessionSampleRate: 1,
      identityPoolId: env.rumIdentityPoolId,
      endpoint: `https://dataplane.rum.${env.rumRegion}.amazonaws.com`,
      telemetries: ["errors", "performance", "http"],
      allowCookies: true,
      enableXRay: false,
    });
  } catch {
    // RUM is best-effort telemetry — never let it break the app.
    started = false;
  }
}
